import type {
  BaseRecord,
  CreateParams,
  CreateResponse,
  CustomParams,
  CustomResponse,
  DataProvider,
  DeleteOneParams,
  DeleteOneResponse,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
  HttpError,
  UpdateParams
} from '@refinedev/core';

import { type ErrorContext, trackDatadogError } from '@/libs/datadog';
import { handleError } from '@/utils/withErrorNotification';

import type { DataProviderEnhancer } from '../types';

export const withErrorNotificationEnhancer: DataProviderEnhancer = (
  provider: DataProvider
) => {
  return {
    ...provider,
    getOne: async <TData extends BaseRecord = BaseRecord>(
      params: GetOneParams
    ): Promise<GetOneResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.getOne,
        'getOne'
      )<TData>(params);
    },
    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams
    ): Promise<GetListResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.getList,
        'getList'
      )<TData>(params);
    },
    create: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: CreateParams<TVariables>
    ): Promise<CreateResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.create,
        'create'
      )<TData>(params as CreateParams<object>);
    },
    update: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: UpdateParams<TVariables>
    ): Promise<CreateResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.update,
        'update'
      )<TData>(params as UpdateParams<object>);
    },
    deleteOne: <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: DeleteOneParams<TVariables>
    ): Promise<DeleteOneResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.deleteOne,
        'deleteOne'
      )<TData>(params as DeleteOneParams<object>);
    },
    custom: <
      TData extends BaseRecord = BaseRecord,
      TQuery = unknown,
      TPayload = unknown
    >(
      params: CustomParams<TQuery, TPayload>
    ): Promise<CustomResponse<TData>> => {
      return wrapWithErrorNotification(
        provider.custom!,
        'custom'
      )<TData>(params);
    }
  };
};

function notifyAndMarkError(error: unknown) {
  const httpError = error as HttpError;
  handleError(httpError);
  httpError.message += '_global-notified';
}

/**
 * Reports server-side and network/unexpected failures to Datadog with the
 * operation and resource attached. Client errors (4xx) are skipped: they are
 * expected validation/permission responses already surfaced to the user, and
 * reporting them would inflate RUM error rates. Reports the untouched error
 * before {@link notifyAndMarkError} mutates its message.
 */
function reportApiError(error: unknown, context: ErrorContext) {
  const httpError = error as HttpError;
  const status = httpError.response?.status ?? httpError.statusCode ?? 0;

  const isServerOrNetworkError = status === 0 || status >= 500;

  if (isServerOrNetworkError) {
    trackDatadogError(error, { ...context, status });
  }
}

function wrapWithErrorNotification<
  Args extends unknown[],
  Return extends Promise<unknown>
>(fn: (...args: Args) => Return, operation: string) {
  return async (...args: Args): Promise<Awaited<Return>> => {
    try {
      return await fn(...args);
    } catch (error) {
      const params = args[0] as {
        resource?: string;
        meta?: { globalErrorNotification?: boolean };
      };

      reportApiError(error, {
        source: 'data-provider',
        operation,
        resource: params?.resource ?? 'n/a'
      });

      const notifyError = params?.meta?.globalErrorNotification ?? true;
      if (notifyError) notifyAndMarkError(error);
      throw error;
    }
  };
}
